import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FlowDetail {
  readonly flowId: string;
  readonly flowCode: string;
  readonly flowName: string;
  readonly integrationType: string;
  readonly sourceConnectorId: string | null;
  readonly targetConnectorId: string | null;
  readonly transformationId: string | null;
  readonly scheduleCron: string | null;
  readonly status: string;
  readonly lastRunStatus: string | null;
  readonly lastRunAt: string | null;
}

/**
 * GR L29.02 — Flow Visual Designer (`/admin/esb/flows/{id}`). Renders a single
 * `esb integration flow` as its source → transform → target pipeline, so the
 * integrator can see how a message moves through the flow and on what schedule.
 */
export function GrEsbFlowDesignerPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [flow, setFlow] = useState<FlowDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: FlowDetail }>(`/v1/eip/flows/${id}`, { token })
      .then((r) => { if (alive) { setFlow(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id]);

  return (
    <main aria-label="esb-designer">
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr29.designer.loading')}</p> : flow && (
        <>
          <h1 aria-label="flow-heading">{flow.flowName} ({flow.status})</h1>
          <p aria-label="flow-meta">
            {flow.flowCode} · {flow.integrationType}
            {flow.scheduleCron ? ` · ${flow.scheduleCron}` : ''}
            {flow.lastRunStatus ? ` · ${t('gr29.designer.lastRun')} ${flow.lastRunStatus}` : ''}
          </p>
          <ol aria-label="flow-pipeline">
            <li aria-label="stage-source">{t('gr29.designer.source')}: {flow.sourceConnectorId ?? t('gr29.designer.none')}</li>
            <li aria-label="stage-transform">{t('gr29.designer.transform')}: {flow.transformationId ?? t('gr29.designer.passthrough')}</li>
            <li aria-label="stage-target">{t('gr29.designer.target')}: {flow.targetConnectorId ?? t('gr29.designer.none')}</li>
          </ol>
        </>
      )}
    </main>
  );
}
